import type { FastifyInstance } from 'fastify'
import type { RouteOptimizationController } from './routeOptimization.controller.js'

export async function routeOptimizationRoutes(
  fastify: FastifyInstance,
  controller: RouteOptimizationController
): Promise<void> {
  // Optimize route order (TSP)
  fastify.post(
    '/plans/:planId/optimize',
    {
      schema: {
        params: { 
          type: 'object', 
          properties: { planId: { type: 'string' } },
          required: ['planId']
        },
        body: {
          type: 'object',
          required: ['stageIds', 'constraints'],
          properties: {
            stageIds: { type: 'array', items: { type: 'string' }, minItems: 2 },
            constraints: {
              type: 'object',
              properties: {
                maxDetourKm: { type: 'number' },
                maxDetourPercent: { type: 'number' },
                budgetEur: { type: 'number' },
                maxDurationDays: { type: 'number' },
                preferScenic: { type: 'boolean' },
                avoidTolls: { type: 'boolean' },
                avoidHighways: { type: 'boolean' },
                profile: { type: 'string', enum: ['eco', 'fast', 'balanced'] },
              },
            },
          },
        },
        response: {
          200: {
            type: 'object',
            properties: {
              planId: { type: 'string' },
              result: { type: 'object' },
              detourRecommendations: { type: 'array' },
              computationTimeMs: { type: 'number' },
            },
          },
        },
      },
    },
    async (request, reply) => {
      return controller.optimizeRoute(request as any, reply)
    }
  )

  // Compare driving profiles
  fastify.post(
    '/plans/:planId/compare-profiles',
    {
      schema: {
        params: { 
          type: 'object', 
          properties: { planId: { type: 'string' } },
          required: ['planId']
        },
        body: {
          type: 'object',
          required: ['stageIds', 'profiles'],
          properties: {
            stageIds: { type: 'array', items: { type: 'string' }, minItems: 2 },
            profiles: { 
              type: 'array', 
              items: { type: 'string', enum: ['eco', 'fast', 'balanced'] },
              minItems: 1
            },
          },
        },
        response: {
          200: {
            type: 'object',
            properties: {
              comparisons: { type: 'array' },
            },
          },
        },
      },
    },
    async (request, reply) => {
      return controller.compareProfiles(request as any, reply)
    }
  )

  // Suggest intelligent detours
  fastify.get(
    '/plans/:planId/detours',
    {
      schema: {
        params: { 
          type: 'object', 
          properties: { planId: { type: 'string' } },
          required: ['planId']
        },
        querystring: {
          type: 'object',
          properties: {
            maxDetourKm: { type: 'string' },
            maxDetourPercent: { type: 'string' },
          },
        },
        response: {
          200: {
            type: 'object',
            properties: {
              recommendations: { type: 'array' },
            },
          },
        },
      },
    },
    async (request, reply) => {
      return controller.suggestDetours(request as any, reply)
    }
  )
}
